import { state } from '../state'

/**
 * Select a filter option and emit the filter event
 * @param {string|number} optionId - The ID of the filter group
 * @param {string|number} itemId - The ID of the selected filter item
 * @param {Object} filterOptions - All available filter options
 * @param {Function} emitFilter - Function to emit the filter event
 */
export const selectFilterItem = (optionId, itemId, filterOptions, emitFilter) => {
  // Initialize the filter array if it doesn't exist
  if (!state.selectedFilters[optionId]) {
    state.selectedFilters[optionId] = []
  }

  // Find the filter option object
  const option = filterOptions.find(opt => opt.id === optionId)
  
  // If option is not found, return
  if (!option) {
    console.warn(`Filter option with id ${optionId} not found`)
    return
  }

  if (option.multiSelect) {
    // Handle multi-select filters
    const index = state.selectedFilters[optionId].indexOf(itemId)
    
    if (index > -1) {
      // Remove item if already selected
      state.selectedFilters[optionId].splice(index, 1)
      
      // If all items are deselected and there's a default, select it
      if (state.selectedFilters[optionId].length === 0) {
        const defaultItem = option.items?.find(item => item.default)
        if (defaultItem) {
          state.selectedFilters[optionId] = [defaultItem.id]
        }
      }
    } else {
      // Add item if not selected
      state.selectedFilters[optionId].push(itemId)
    }
  } else {
    // Handle single-select filters
    if (state.selectedFilters[optionId][0] === itemId) {
      // If current item is deselected, select default if exists
      const defaultItem = option.items?.find(item => item.default)
      if (defaultItem) {
        state.selectedFilters[optionId] = [defaultItem.id]
      } else {
        // Clear selection if no default exists
        state.selectedFilters[optionId] = []
      }
    } else {
      // Select new item
      state.selectedFilters[optionId] = [itemId]
    }
  }

  // Create filter object for emitting
  const filters = {}
  for (const [key, value] of Object.entries(state.selectedFilters)) {
    if (value && Array.isArray(value) && value.length > 0) {
      filters[key] = value.join(',')
    }
  }

  // Emit the filter event with all selected filters
  emitFilter(filters)

  // Close the menu for single-select filters
  if (!option.multiSelect) {
    state.openMenu.value = null
  }
}

/**
 * Initialize default filter selections and emit initial filter event
 * @param {Array} filterOptions - All available filter options
 * @param {Function} emitFilter - Function to emit the filter event
 */
export const initializeDefaultFilters = (filterOptions, emitFilter) => {
  const initialFilters = {}
  
  // For each filter option, check if there's a default item
  filterOptions.forEach(option => {
    const defaultItem = option.items?.find(item => item.default)
    
    if (defaultItem && !state.selectedFilters[option.id]) {
      // Set the default selection in state
      state.selectedFilters[option.id] = [defaultItem.id]
      
      // Add to initial filters object
      initialFilters[option.id] = defaultItem.id.toString()
    }
  })
  
  // If we have any default filters, emit them
  if (Object.keys(initialFilters).length > 0) {
    emitFilter(initialFilters)
  }
}

/**
 * Reset all filter selections to defaults
 * @param {Array} filterOptions - All available filter options
 * @param {Function} emitFilter - Function to emit the filter event
 */
export const resetFilters = (filterOptions, emitFilter) => {
  // Reset to default values
  const defaultFilters = {}
  
  // For each filter option, find default items
  filterOptions.forEach(option => {
    const defaultItem = option.items?.find(item => item.default)
    
    if (defaultItem) {
      // Set the default selection in state
      state.selectedFilters[option.id] = [defaultItem.id]
      
      // Add to default filters object
      defaultFilters[option.id] = defaultItem.id.toString()
    } else {
      // If no default exists, clear this filter
      state.selectedFilters[option.id] = []
    }
  })
  
  // Emit the filter event with default filters
  emitFilter(defaultFilters)
}
